/**
 * Finanzas → Transferencias (`/finanzas/transferencias`).
 *
 * Portado de `Bank_transfers` + `views/finance/bank_transfers/index.php` y de su
 * bundle `src/finance/bank_transfers`, que pedía los datos a la API interna.
 *
 * La pantalla es **el control de las transferencias bancarias**: las ventas que se
 * cobraron por transferencia se listan con su comprobante y, cuando el dinero
 * aparece en la cuenta, se **verifican** —lo que crea el movimiento bancario y
 * marca la venta—. Arriba van los dos contadores que se miran primero: cuánto
 * *está por verificar* y cuánto *ya está verificado*.
 *
 * Reglas heredadas:
 * - Se ven las ventas **vivas** cuyo método de pago (primario o secundario) es
 *   `TRANSFERENCIA`, y cada una aparece como una fila propia: el mismo documento
 *   puede estar dos veces, una por cada método.
 * - El filtro de fechas es por **fecha de la venta** y el rango por defecto es
 *   desde el 1 de enero hasta hoy, como el listado original.
 * - La verificación **no deja cambiar el importe**: es el de la venta.
 * - Anular el movimiento **libera la venta**, que vuelve a quedar pendiente de
 *   verificar; el comprobante del movimiento se borra.
 * - Las filas verificadas llevan los botones del movimiento (verlo, editarlo y
 *   anularlo); las pendientes, el de verificar.
 *
 * Diferencias anotadas:
 * - El listado se pagina y se filtra en el servidor (el original traía todas las
 *   filas y las paginaba en el navegador), con buscador por documento, cliente y
 *   referencia.
 * - Los dos contadores son del **filtro entero**, no de la página.
 */

import {
  BadgeCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Info,
  Pencil,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import {
  DialogoDetalleTransferencia,
  DialogoMovimientoTransferencia,
  DialogoVerificarTransferencia,
} from '../components/transferencia-dialogos';
import type { DatosTransferencia } from '../components/transferencia-dialogos';
import {
  ESTADOS,
  useActualizarMovimientoTransferencia,
  useAnularMovimientoTransferencia,
  useMovimientoTransferencia,
  useTransferencias,
  useVentaTransferencia,
  useVerificarTransferencia,
} from '../transferencias.api';
import type {
  EstadoTransferencias,
  FiltrosTransferencias,
  MovimientoTransferencia,
  Transferencia,
} from '../transferencias.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tono del distintivo según el documento de la venta. */
const TONOS: Record<string, 'brand' | 'success' | 'neutral'> = {
  boleta: 'brand',
  factura: 'success',
  nota_venta: 'neutral',
};

export function TransferenciasPage() {
  useDocumentTitle('Finanzas · Transferencias');

  const [desde, setDesde] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [hasta, setHasta] = useState(() => toIsoDate(new Date()));
  const [estado, setEstado] = useState<EstadoTransferencias>('pendientes');
  const [turno, setTurno] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [paraVerificar, setParaVerificar] = useState<Transferencia | null>(null);
  const [detalle, setDetalle] = useState<Transferencia | null>(null);
  const [movimientoAbierto, setMovimientoAbierto] = useState<number | null>(null);
  const [editandoMovimiento, setEditandoMovimiento] = useState(false);
  const [porAnular, setPorAnular] = useState<MovimientoTransferencia | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosTransferencias>(
    () => ({
      desde,
      hasta,
      estado,
      turno: turno ? Number(turno) : 0,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [desde, hasta, estado, turno, busqueda, pagina, limite],
  );

  const consulta = useTransferencias(filtros);
  const verificar = useVerificarTransferencia();
  const actualizar = useActualizarMovimientoTransferencia();
  const anular = useAnularMovimientoTransferencia();

  /** La transferencia que tiene una ventana abierta (verificar o detalle). */
  const seleccionada = paraVerificar ?? detalle;
  const tipoSeleccionado = seleccionada
    ? `${seleccionada.tipo_venta}_${seleccionada.tipo_metodo}`
    : null;
  const venta = useVentaTransferencia(tipoSeleccionado, seleccionada?.venta_id ?? null);

  const movimiento = useMovimientoTransferencia(movimientoAbierto);

  const transferencias = consulta.data?.transferencias ?? [];
  const totales = consulta.data?.totales;
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;
  const cuentas = consulta.data?.cuentas ?? [];
  const metodos = consulta.data?.metodos_pago ?? [];

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const confirmarVerificacion = (datos: DatosTransferencia) => {
    if (!paraVerificar) return;

    setError(null);

    verificar.mutate(
      {
        tipo: `${paraVerificar.tipo_venta}_${paraVerificar.tipo_metodo}`,
        venta_id: String(paraVerificar.venta_id),
        cuenta_id: datos.cuenta_id,
        metodo_pago_id: datos.metodo_pago_id,
        fecha: datos.fecha,
        referencia: datos.referencia,
        notas: datos.notas,
        comprobante: datos.comprobante,
      },
      {
        onSuccess: (resultado) => {
          setParaVerificar(null);
          setAviso(
            `Transferencia verificada: ${paraVerificar.tipo_venta_etiqueta} ${
              paraVerificar.documento ?? ''
            } por ${formatCurrency(paraVerificar.monto)} (movimiento #${resultado.movement_id}).`,
          );
          setTimeout(() => setAviso(null), 8000);
        },
        onError: (fallo) => {
          setError(fallo instanceof Error ? fallo.message : 'No se pudo verificar la transferencia.');
        },
      },
    );
  };

  const guardarMovimiento = (id: number, datos: DatosTransferencia) => {
    setError(null);

    actualizar.mutate(
      {
        id,
        datos: {
          cuenta_id: datos.cuenta_id,
          metodo_pago_id: datos.metodo_pago_id,
          fecha: datos.fecha,
          referencia: datos.referencia,
          notas: datos.notas,
          comprobante: datos.comprobante,
        },
      },
      {
        onSuccess: () => {
          setAviso(`Movimiento #${id} actualizado.`);
          setTimeout(() => setAviso(null), 6000);
        },
        onError: (fallo) => {
          setError(fallo instanceof Error ? fallo.message : 'No se pudo actualizar el movimiento.');
        },
      },
    );
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);

    anular.mutate(porAnular.id, {
      onSuccess: () => {
        setAviso(
          `Movimiento #${porAnular.id} anulado. La transferencia volvió a quedar pendiente de verificar.`,
        );
        setPorAnular(null);
        setMovimientoAbierto(null);
        setTimeout(() => setAviso(null), 8000);
      },
      onError: (fallo) => {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo anular el movimiento.');
        setPorAnular(null);
      },
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Transferencias</h1>
          <p className="mt-1 text-sm text-ink-500">
            Las ventas cobradas por transferencia bancaria: aquí se comprueban contra la cuenta y se
            crea su movimiento. Anularlo devuelve la transferencia a la lista de pendientes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error && !paraVerificar && !movimientoAbierto ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="date"
                value={desde}
                max={hasta}
                onChange={(evento) => filtrar(() => setDesde(evento.target.value))}
                aria-label="Fecha de inicio"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="date"
                value={hasta}
                min={desde}
                onChange={(evento) => filtrar(() => setHasta(evento.target.value))}
                aria-label="Fecha de fin"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Turno
              <Select
                value={turno}
                onChange={(evento) => filtrar(() => setTurno(evento.target.value))}
                className={CONTROL}
                aria-label="Turno"
              >
                <option value="">Todos los turnos</option>
                {(consulta.data?.turnos ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Estado
              <Select
                value={estado}
                onChange={(evento) =>
                  filtrar(() => setEstado(evento.target.value as EstadoTransferencias))
                }
                className={CONTROL}
                aria-label="Estado"
              >
                {ESTADOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Documento, cliente, referencia…"
                  className="pl-9"
                  aria-label="Buscar transferencia"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={cn(CONTROL, 'w-24')}
                aria-label="Filas por página"
              >
                {(consulta.data?.tamanos ?? [10, 25, 50, 100]).map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} transferencia{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Contadores del rango */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700"
            >
              <Clock className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Por verificar
              </p>
              <p className="text-base font-bold break-words text-amber-800 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_pendiente ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.pendientes ?? 0).toLocaleString('es-PE')} transferencia(s)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"
            >
              <BadgeCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Verificadas
              </p>
              <p className="text-base font-bold break-words text-emerald-800 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_verificado ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.verificadas ?? 0).toLocaleString('es-PE')} transferencia(s)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {ESTADOS.map((opcion) => (
              <button
                key={opcion.valor}
                type="button"
                aria-pressed={estado === opcion.valor}
                onClick={() => filtrar(() => setEstado(opcion.valor))}
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  estado === opcion.valor
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                )}
              >
                {opcion.valor === 'verificadas' ? (
                  <CheckCircle2 className="size-3.5" aria-hidden="true" />
                ) : opcion.valor === 'pendientes' ? (
                  <Clock className="size-3.5" aria-hidden="true" />
                ) : null}
                {opcion.etiqueta}
              </button>
            ))}
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer las transferencias">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">Estado</th>
                  <th className="px-2 py-2 whitespace-nowrap">Turno</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2">Documento</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Monto</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 9 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : transferencias.map((fila) => (
                      <tr key={`${fila.tipo_venta}-${fila.tipo_metodo}-${fila.venta_id}`} className="border-t border-ink-100">
                        <td className="px-2 py-2">
                          {fila.verificado ? (
                            <Badge variant="success">Verificada</Badge>
                          ) : (
                            <Badge variant="warning">Pendiente</Badge>
                          )}
                        </td>

                        <td className="px-2 py-2 font-semibold whitespace-nowrap text-ink-800">
                          {fila.turno ? `#${fila.turno}` : '—'}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <p>{formatDateValue(fila.fecha_venta)}</p>
                          <p className="text-[11px] text-ink-500">{fila.hora ?? '—'}</p>
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={TONOS[fila.tipo_venta] ?? 'neutral'}>
                            {fila.tipo_venta_etiqueta}
                          </Badge>
                          <p className="mt-0.5 text-[11px] text-ink-500">
                            método {fila.tipo_metodo}
                          </p>
                        </td>

                        <td className="px-2 py-2 font-semibold whitespace-nowrap text-ink-800">
                          {fila.documento ?? '—'}
                        </td>

                        <td className="max-w-[12rem] px-2 py-2">
                          <span className="block truncate" title={fila.referencia ?? ''}>
                            {fila.referencia || '—'}
                          </span>
                        </td>

                        <td className="max-w-[16rem] px-2 py-2">
                          <p className="truncate" title={fila.cliente}>
                            {fila.cliente}
                          </p>
                          {fila.cliente_documento ? (
                            <p className="truncate text-[11px] text-ink-500">
                              {fila.cliente_documento}
                            </p>
                          ) : null}
                        </td>

                        <td className="px-2 py-2 text-right font-semibold whitespace-nowrap text-emerald-700 tabular-nums">
                          {formatCurrency(fila.monto)}
                        </td>

                        <td className="px-2 py-2">
                          <div className="flex justify-end gap-1">
                            {fila.verificado ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="Ver el detalle de la transferencia"
                                  aria-label={`Ver el detalle de la transferencia ${fila.documento ?? fila.venta_id}`}
                                  onClick={() => setDetalle(fila)}
                                >
                                  <Eye className="size-4" aria-hidden="true" />
                                </Button>

                                {fila.movement_id ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      title="Ver el movimiento bancario"
                                      aria-label={`Ver el movimiento ${fila.movement_id}`}
                                      onClick={() => {
                                        setEditandoMovimiento(false);
                                        setMovimientoAbierto(fila.movement_id);
                                      }}
                                    >
                                      <FileText className="size-4" aria-hidden="true" />
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      title="Editar el movimiento"
                                      aria-label={`Editar el movimiento ${fila.movement_id}`}
                                      onClick={() => {
                                        setEditandoMovimiento(true);
                                        setMovimientoAbierto(fila.movement_id);
                                      }}
                                    >
                                      <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                                    </Button>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="primary"
                                leftIcon={<CheckCircle2 className="size-3.5" aria-hidden="true" />}
                                onClick={() => setParaVerificar(fila)}
                              >
                                Verificar
                              </Button>                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Verificar una transferencia crea el movimiento bancario por el importe de la venta y deja
        la venta marcada con quién la verificó y cuándo. Si se anula el movimiento, la venta vuelve
        a esta lista como pendiente y su comprobante se borra del almacén.
      </p>

      {paraVerificar ? (
        <DialogoVerificarTransferencia
          key={`verificar-${paraVerificar.tipo_venta}-${paraVerificar.tipo_metodo}-${paraVerificar.venta_id}`}
          transferencia={paraVerificar}
          venta={venta.data}
          cargando={venta.isLoading}
          hoy={consulta.data?.hoy ?? toIsoDate(new Date())}
          guardando={verificar.isPending}
          error={error}
          onCerrar={() => {
            setParaVerificar(null);
            setError(null);
          }}
          onVerificar={confirmarVerificacion}
        />
      ) : null}

      {detalle ? (
        <DialogoDetalleTransferencia
          key={`detalle-${detalle.tipo_venta}-${detalle.tipo_metodo}-${detalle.venta_id}`}
          transferencia={detalle}
          venta={venta.data}
          cargando={venta.isLoading}
          onCerrar={() => setDetalle(null)}
        />
      ) : null}

      <DialogoMovimientoTransferencia
        key={`movimiento-${movimientoAbierto ?? 'cerrado'}-${editandoMovimiento ? 'editar' : 'ver'}`}
        movimientoId={movimientoAbierto}
        consulta={movimiento.data}
        cargando={movimiento.isLoading}
        cuentas={cuentas}
        metodos={metodos}
        guardando={actualizar.isPending}
        error={movimientoAbierto ? error : null}
        abrirEditando={editandoMovimiento}
        onCerrar={() => {
          setMovimientoAbierto(null);
          setError(null);
        }}
        onGuardar={guardarMovimiento}
        onAnular={(fila) => setPorAnular(fila)}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title="Anular el movimiento"
        description={
          porAnular
            ? `Se anulará el movimiento #${porAnular.id} (${formatCurrency(
                porAnular.monto,
              )}) y la transferencia de ${porAnular.detalle.document_number ?? 'la venta'} volverá a quedar pendiente de verificar. El comprobante del movimiento se borra.`
            : ''
        }
        confirmLabel="Anular movimiento"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
